/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {MutableRefObject} from 'react';
import {GridApi} from 'ag-grid-community';
import invert from 'lodash.invert';
import isBoolean from 'lodash.isboolean';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getEventList} from '@ion/core/src/services/EventCodes';
import {
  AlertSeverity,
  CrashboxxSeverityCode,
  CrashClassification,
  EntityStatus,
  GenericObject
} from '@ion/core/src/types/entities';
import {FilterTypes, SortDirection} from '@ion/core/src/types/services';
import {
  stringToBoolean
} from '@ion/core/src/utils/BooleanUtils';
import {
  DefaultDateFormats,
  endOfDay,
  isSame,
  startOfDay,
  toISOString
} from '@ion/core/src/utils/DateUtil';
import {
  deepCopy
} from '@ion/core/src/utils/ObjectUtil';
import {
  getMatchingCharacters,
  titleCase
} from '@ion/core/src/utils/StringUtil';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';
import {ScheduleStatusServiceFields} from '../types/types';
import {ColumnDefinitionType} from './ColumnDefinitions';

// Types for using the Select All Checkbox in the grid header.
export interface SelectAllCheckbox {
  getAreTotalRowsSelected?: (areTotalRowsSelected: boolean) => void;
  enableSelectAllCheckbox?: boolean;
}

// defines the Filter Model interface...
export interface FilterModel {
  filterType: string;
  type: string;
  filter: string;
};
export interface FilterModelType {
  [key: string]: {
    dateFrom?: string | null;
    dateTo?: string | null;
    filter?: string;
    values?: any[];
    filterType: string;
    type: string;
  }
}

// Shared type for select all / total grid rows functionality
export interface SelectedRows {
  setAreTotalRowsSelected?: (areTotalRowsSelected: boolean) => void;
  areTotalRowsSelected?: boolean;
  totalRowCount?: number;
}

export interface SortModelType {
  colId: string;
  sort: 'asc' | 'desc'
}

/**
 * @normalizeAccountField
 * normalizeAccountField helps to normalize the Account Field to CTC
 */
export const normalizeAccountField = {
  'account.title' : 'account.name'
};

/**
 * Enumeration representing specific class names used in the AG Grid CSS.These class names
 * are intended to be applied to AG Grid components to control their layout or appearance.
 */
export enum AG_CLASSES {
  BODY_LAYOUT = '& .ag-body.ag-layout-normal'
};

// defines the params structure for validateRequest
export interface ValidateRequestParams {
  filterModel: FilterModelType;
};

// defines the result structure for validateRequest
export interface ValidateRequestResults {
  valid: boolean;
  message: string;
  allMessages: string[];
};

// defines the result structure for getPageProxy method...
interface PageProxy {
  currentPage: number;
  pageSize: number;
  bottomDisplayedRowIndex: number;
  topDisplayedRowIndex: number;
};

// defines the sort model structure...
interface SortModel {
  colId: string;
  sort: 'asc' | 'desc';
}

/**
 * Converts an AG Grid Sort Model to a valid CTC Sort Parameters
 * list.
 *
 * @convertSortModel
 */
export const convertSortModel = (
  sortModel: SortModelType[],
  replacements?: GenericObject,
  queryBased = false
): string[] => {
  const results = [];
  if (sortModel?.length && !isEmpty(sortModel[0])) {
    sortModel.forEach(({colId, sort}) => {
      let attr = replacements ? replacements[colId] || colId : colId;
      attr = normalizeAccountField[attr] || attr;
      if (queryBased) {
        results.push(`${attr} ${sort}`);
      } else {
        results.push(`${sort !== 'asc' ? SortDirection.Descending : ''}${attr}`);
      }
    });
  }
  return results;
};

/**
 * Converts an AG Grid Filter Model to a valid CTC Filter Object
 *
 * @convertFilterModel
 */
export const convertFilterModel = (
  filterModel: FilterModelType = {},
  replacements: GenericObject = {}
): GenericObject => {
  let multiFilterObj = {};
  const filterData = Object.entries(filterModel);
  filterData.forEach(item => {
    let obj = {};
    const [columnName, data = {filterType: '', filter: ''}] = deepCopy(item);
    const {filterType, filter} = data;
    if (columnName === 'deviceType') {
      obj = getDeviceTypeData(data, replacements);
      multiFilterObj = Object.assign(multiFilterObj, obj);
    }
    if (columnName === 'inheritable') {
      obj = {
        [replacements[columnName] || columnName] : stringToBoolean(filter)
      };
      multiFilterObj = Object.assign(multiFilterObj, obj);
    }
    if (columnName === 'landmarkStatus') {
      obj = {
        [replacements[columnName] || columnName] : filter
      };
      multiFilterObj = Object.assign(multiFilterObj, obj);
    }
    if (columnName === 'status') {
      data.filter = getStatusFilters(filter, item, filterType);
    }
    if (columnName === 'severity') {
      const options = invert(translate('common:alertSeverity', {returnObjects: true}));
      data.filter   = getSeverityType(getAbsSearchTerm(filter, options), data);
    }
    if (columnName === 'isAcknowledged') {
      data.filter = getAcknowledgementTerm(filter);
    }
    if (columnName === 'severityCode') {
      data.filter = getAbsSearchTerm(filter, CrashboxxSeverityCode);
    }
    if (columnName === 'severityUI') {
      const options = invert(translate('crashboxx:severityCode', {returnObjects: true}));
      data.filter   = getAbsSearchTerm(filter, options);
    }
    if (columnName === 'eventClassification') {
      data.filter = getAbsSearchTerm(filter, CrashClassification);
    }
    if (columnName === 'eventClassificationUI') {
      const options = invert(translate('crashboxx:eventClassification', {returnObjects: true}));
      data.filter   = getAbsSearchTerm(filter, options);
    }
    if (columnName === 'nextServiceProgress') {
      const options = invert(translate('maintenance:scheduleStatusServiceFields', {returnObjects: true}));
      const matched = getAbsSearchTerm(filter, options);
      data.filter   = ScheduleStatusServiceFields[matched];
    }
    if (columnName === 'deviceUI' && filter.startsWith('ESN')) {
      const deviceId = filter.replace(/[^0-9]/g, ''); // extract the ESN digits only
      data.filter    = deviceId || filter;
    }
    if (['account', 'accountUI', 'group', 'groupUI', 'parent'].includes(columnName)) {
      multiFilterObj = {...multiFilterObj, [replacements[columnName] || columnName]: filter};
    }
    if (!filterType) {
      obj = null;
    }
    if (isEmpty(obj)) {
      obj = {
        [replacements[columnName] || columnName] : filterType === 'date'
          ? getDateFilter(data)
          : getTextFilter(data)
      };
    }
    if (obj) {
      multiFilterObj = Object.assign(multiFilterObj, obj);
    }
  });
  return isEmpty(multiFilterObj) ? null : multiFilterObj;
};

/**
 * Takes text values and filter type from the filter model and
 * returns them in appropriate form for CTC to be able to ingest
 * based on the type.
 *
 * @getTextFilter
 */
export const getTextFilter = (data) => {
  const {filter, type} = data;
  switch (type) {
    case FilterTypes.Contains:
      return `*${filter}*`;
    case FilterTypes.StartsWith:
      return `${filter}*`;
    case FilterTypes.EndsWith:
      return `*${filter}`;
    case FilterTypes.NotEqual:
      return `-${filter}`;
    case FilterTypes.Equals:
      return filter;
    default:
      return filter;
  }
};

/**
 * Takes date values from the filter model and returns them in
 * appropriate form for CTC to be able to ingest based on the type.
 *
 * @getDateFilter
 */
export const getDateFilter = (data) => {
  const {dateFrom, dateTo, type} = data;
  switch (type) {
    case FilterTypes.InRange:
      return `[${toISOString(dateFrom, 'startOf')} TO ${toISOString(dateTo, 'endOf')}]`;
    case FilterTypes.GreaterThan:
      return `[${toISOString(dateFrom, 'endOf')} TO *]`;
    case FilterTypes.LessThan:
      return `[* TO ${toISOString(dateFrom, 'startOf')}]`;
    case FilterTypes.Equals:
      return `[${toISOString(dateFrom, 'startOf')} TO ${toISOString(dateFrom, 'endOf')}]`;
    default:
      return toISOString(dateFrom, 'startOf');
  }
};

/**
 * Function that tries to match what the user typed to the closest
 * Status type if more than 3 characters match.
 *
 * @getStatusFilter
 */
export const getStatusFilter = (filter, item) => {
  if (filter) {
    const matches = {charMatches: 0, entity: EntityStatus.Enabled};
    Object.values(EntityStatus).forEach((entity) => {
      const characterMatches = getMatchingCharacters(filter, entity, false);
      if (characterMatches > 3) {
        if (characterMatches > matches.charMatches) {
          matches.charMatches = characterMatches;
          matches.entity = EntityStatus[entity];
        }
      }
    });
    return matches.entity;
  } else {
    const {values} = item[1];
    const filterValues = values.toString();
    return filterValues;
  }
};

/**
 * Devices need to be filtered based on all matching devices
 * with the given entityId, so the href needs to be passed. Here
 * we need to find out which device types the query matches
 * (or partially), then provide the filter with that matching type's href.
 *
 * @getDeviceTypeData
 */
export const getDeviceTypeData = (
  {filter}: GenericObject,
  attributeMappings: GenericObject = {}
): { type: string; } => {
  const {deviceType} = attributeMappings;
  const deviceTypes = getDeviceTypeHrefs(deviceType);
  let deviceHref;
  for (const [type, href] of Object.entries(deviceTypes)) {
    const characterMatches = getMatchingCharacters(filter, type, false);
    if ((characterMatches / type.length) > 0.2) {
      deviceHref = href;
    }
  }
  return {type: deviceHref};
};

/**
 * Returns an object of device names with their
 * corresponding hrefs so that the hrefs can be provided to
 * the filter.
 *
 * @getDeviceTypeHrefs
 */
export const getDeviceTypeHrefs = (
  devices: GenericObject[] = []
): GenericObject => {
  const deviceTypes = {};
  devices.forEach(attribute => {
    const {type: {title, href}} = attribute;
    deviceTypes[title] = href;
  });
  return deviceTypes;
};

/**
 * A function that tries to match the key to the closest Severity type
 * if more than or equals to half characters in the string match.
 *
 * ```typescript
 *
 * import {getSeverityType} from '@ion/dls/src/grid/IonGridUtils';
 *
 * const severityType = getSeverityType(filter, data);
 *
 * ```
 *
 * @param filter A filter key that user typed in the column header.
 * @param data Filter model data
 * @returns default Filter which is excluding all type of severity
 *          if none of AlertSeverity is matching with the given filter key.
 *          Otherwise returns matched AlertSeverity type
 */
export const getSeverityType = (filter: string, data: GenericObject): string => {
  const matches = {charMatches: 0, entity: undefined};
  const {type} = data;
  let defaultFilter = '';
  const comparator = filter.toLowerCase();
  Object.values(AlertSeverity).forEach((entity) => {
    const characterMatches = getMatchingCharacters(comparator, entity, false);
    const minMatch = Math.round(entity?.length / 2);
    if (characterMatches >= minMatch && characterMatches > matches.charMatches) {
      matches.charMatches = characterMatches;
      matches.entity = AlertSeverity[entity];
    }
    /* Constructing default filter to use in case of none of the severity type
       matched with the given filter key */
    defaultFilter = defaultFilter + getSearchPrefix(defaultFilter, type) + entity;
  });
  return matches.entity ? matches.entity : defaultFilter;
};

/**
 * The private function supports the construction of the default filter key for Severity Type filtering.
 *
 * @param filter The filter string of the construction
 * @param type The type of search.
 *
 * @returns The prefix for the each filter based on the type.
 */
const getSearchPrefix = (filter: string, type: FilterTypes): string => {
  const empty = isEmpty(filter);
  return {
    [FilterTypes.Equals]   : `${empty ? '-' : ', -'}`,
    [FilterTypes.NotEqual] : `${empty ? '' : ', -'}`
  }[type] || filter;
};

/**
 * The utility function provides the ability to get the current row count of
 * the grid.
 *
 * ```typescript
 *
 *  import {getGridRowCount} from '@ion/dls/src/grid/IonGridUtils';
 *
 *  const [gridApi, setGridApi]   = useState<GridApi>(null);
 *  const [rowCount, setRowCount] = useState(0);
 *
 *  setRowCount(getGridRowCount(gridApi));
 *
 * ```
 *
 * @param gridApi The api instance for ag grid.
 * @returns The displayed row count in grid.
 */
export const getGridRowCount = (gridApi: GridApi) => {
  return gridApi?.getDisplayedRowCount() || 0;
};

/**
 * The utility function returns the absoulte matched search terms of any defined enums
 * for CTC services(fixed searchable property).
 *
 * ```typescript
 *
 *  import {getAbsSearchTerm} from '@ion/dls/src/grid/';
 *
 *  const absFilter = getAbsSearchTerm(filter, CrashboxxSeverityCode); // search for crash severity
 *
 *  const absFilter = getAbsSearchTerm(filter, CrashClassification); // search for crash classification
 *
 * ```
 */
export const getAbsSearchTerm = (filter: string, src: GenericObject): string => {
  let searchTerm     = filter;
  const normalizeKey = (key: string) => key?.replaceAll(' ', '')
    .replaceAll('-', '')
    .replaceAll('_', '')
    .toLowerCase();
  const key = normalizeKey(filter);
  if (key) {
    Object.keys(src).some((entity) => {
      if (normalizeKey(entity) === key) {
        return (searchTerm = src[entity]);
      }
      return false;
    });
  }
  return searchTerm;
};

/**
 * The utility function returns the boolean value if the both definitions have the change with respect to the
 * required properties of the definitions. Those properties are hide, sort, and sortIndex.
 *
 * ```typescript
 *
 *  import {hasChangeInDefinitions} from '@ion/dls/src/grid/';
 *
 *  const hasChange = hasChangeInDefinitions(definitions1, definitions2);
 *
 * ```
 */
export const hasChangeInDefinitions = (
  current: ColumnDefinitionType[],
  update: ColumnDefinitionType[]
): boolean => {
  const columnFilter = ({headerName, aggFunc, rowGroup, includeInCompare}) => (
    !!headerName || rowGroup || !!aggFunc || includeInCompare
  );
  const currentDefinitions = current?.filter(columnFilter);
  const updatedDefinitions = update?.filter(columnFilter);
  return currentDefinitions?.some(({
    field: field1,
    hide: hide1 = false,
    sort: sort1 = null,
    sortIndex: sortIndex1 = null,
    totalRowCount: totalRowCount1,
    pageSize: pageSize1,
    rowGroup: rowGroup1,
    editable: editable1 = false
  }, position) => {
    const {
      field,
      hide = false,
      sort = null,
      sortIndex = null,
      totalRowCount,
      rowGroup,
      pageSize,
      editable = false
    } = updatedDefinitions?.[position] || {};
    if (field) {
      let fieldChange = (
        field !== field1 || hide !== hide1 ||
        sort !== sort1 || sortIndex !== sortIndex1 ||
        totalRowCount !== totalRowCount1 ||
        pageSize !== pageSize1 ||
        editable !== editable1
      );
      if (isBoolean(rowGroup) && isBoolean(rowGroup1)) {
        fieldChange = (fieldChange || rowGroup !== rowGroup1);
      }
      return fieldChange;
    }
    return true;
  });
};

/**
 * The utility function returns a boolean value based on changes in the column 
 * order between the `visibleColumns` (current column) and `columnOrderRef` (i.e.
 * the previous column). It returns `true` if there are changes detected in both;
 * otherwise, it returns `false`. If the `orderEnabled` value is `false`, it will
 * directly returns `false`.
 *
 * ```typescript
 *  import {
 *    hasChangeInColumnOrder
 *  } from '@ion/dls/src/grid/IonGridUtils';
 *
 *  const hasChangeInOrder = hasChangeInColumnOrder(
 *    columnOrderRef, columns, true
 *  ); // true if changes in both, otherwise, false ...
 *
 * ```
 */
export const hasChangeInColumnOrder = (
  columnOrderRef: MutableRefObject<string[]>,
  visibleColumns: ColumnDefinitionType[],
  orderEnabled = false
): boolean => {
  if (orderEnabled) {
    const visibleColIds   = visibleColumns?.map(({colId}) => colId);
    const hasOrderChanged = columnOrderRef?.current?.some((column, index) => (
      column !== visibleColIds[index]
    ));
    columnOrderRef.current = visibleColIds;
    return hasOrderChanged;
  }
  return false;
};

/**
 * The function supports the construction of the default filter key for Acknowledge Column
 * in Notification Events Grid.
 */
const getAcknowledgementTerm = (filter: string) => {
  if (filter) {
    const status = {
      [translate('notification-events:yes')] : true,
      [translate('notification-events:no')]  : false
    };
    return status[titleCase(filter.toLowerCase())];
  }
  return '';
};

/**
 * Builds a column definition for an row action pinned to the right.
 * @param cellRenderer the cell renderer to use to render the icon for the row actions.
 * @param width how wide the action column should be (defaults to 48 px to accomodate default icon size).
 * @returns column definition
 */
export const getPinnedRowActionColumnDefinition = (cellRenderer: string, width = 48): ColumnDefinitionType => ({
  cellRenderer       : cellRenderer,
  cellRendererParams : ({data}) => ({data}),
  sortable           : false,
  filter             : false,
  resizable          : true,
  unSortIcon         : false,
  hide               : false,
  width              : width,
  minWidth           : width,
  maxWidth           : width,
  suppressMovable    : true,
  floatingFilter     : false,
  pinned             : 'right',
  lockPosition       : true,
  lockPinned         : true,
  rowDrag            : false,
  menuTabs           : []
});

export const selectedRowStyles = (disable = false) => {
  const defaultStyle = {
    paddingLeft  : '14px',
    paddingRight : '32px'
  };
  const disableStyle = {
    pointerEvents : 'none',
    opacity       : '0.4'
  };
  return disable ? {...defaultStyle, ...disableStyle} : defaultStyle;
};

/**
 * The filterData function takes an array of objects (data) and a filter model
 * (filterModel) as parameters. It returns a filtered array of objects based on
 * the provided filter criteria.
 *
 * @param data - An array of objects representing the data to be filtered.
 * @param filterModel - An object containing filter criteria for each column.
 * @returns An array of objects representing the filtered data.
 *
 * ```typescript
 *
 *  import {filterData} from '@ion/dls/src/grid/';
 *
 *  const filteredData = filterData(dataArray, {
 *    columnName: {filterType: 'text', filter: 'example'}
 *  });
 *
 * ```
 */
export const filterData = (data: GenericObject[], filterModel: GenericObject, augumenter?: Function) => {
  const eventList = getEventList();
  return data.filter(item => {
    const agumented = augumenter?.(item, eventList) || item;
    return Object.entries(filterModel).every(([column, filter]) => {
      const columnValue = agumented[column];
      switch (filter.filterType.toLowerCase()) {
        case 'number':
          return applyNumberFilter(columnValue, filter);
        case 'date':
          return applyDateFilter(columnValue, filter);
        case 'set':
          return applySetFilter(columnValue, filter);
        default:
          return applyTextFilter(columnValue, filter);
      }
    });
  });
};

/**
 * Applies a set filter by checking if the provided value exists in the filter values.
 *
 * ```typescript
 *
 *  import {applySetFilter} from '@ion/dls/src/grid/';
 *
 *  applySetFilter('example', {type: FilterTypes.Set, values: ['example']}); // true
 *
 * ```
 * @param value The value to check against the filter.
 * @param filter The set filter object containing values to filter.
 * @returns A boolean indicating whether the value is included in the filter values.
 */
export const applySetFilter = (value: string, filter: GenericObject) => {
  const {values} = filter || {values: []};
  return values.includes(value);
};

/**
 * The applyTextFilter function applies Text Filter to a given value based on the provided filter object.
 *
 * @param value - The value to filter.
 * @param filter - The filter object containing type and filter criteria.
 * @returns True if the value passes the filter, otherwise false.
 *
 * ```typescript
 *
 *  import {applyTextFilter} from '@ion/dls/src/grid/';
 *
 *  applyTextFilter('example', {type: FilterTypes.Contains, filter: 'exam'}); // true
 *
 * ```
 */
export const applyTextFilter = (value: string, filter: GenericObject) => {
  const valueInLowerCase = value?.toLowerCase();
  const filterValue = filter.filter?.toLowerCase();

  switch (filter.type) {
    case FilterTypes.Contains:
      return valueInLowerCase?.includes(filterValue);
    case FilterTypes.NotContains:
      return !valueInLowerCase?.includes(filterValue);
    case FilterTypes.Equals:
      return valueInLowerCase === filterValue;
    case FilterTypes.NotEqual:
      return valueInLowerCase !== filterValue;
    case FilterTypes.StartsWith:
      return valueInLowerCase?.startsWith(filterValue);
    case FilterTypes.EndsWith:
      return valueInLowerCase?.endsWith(filterValue);
    default:
      return true;
  }
};

/**
 * The applyNumberFilter function applies a numerical filter to a numeric value.
 *
 * @param value - The numeric value to be filtered.
 * @param filter - The filter object containing filter details.
 * @returns True if the date value satisfies the filter conditions, false otherwise.
 *
 * ```typescript
 *
 *  import {applyNumberFilter} from '@ion/dls/src/grid/';
 *
 *  applyNumberFilter(9, {type: FilterTypes.Equals, filter: 9 }); // true
 *
 * ```
 */
export const applyNumberFilter = (value: number, filter: GenericObject) => {
  const {type, filter: filterValue, from, to} = filter;

  switch (type) {
    case FilterTypes.Equals:
      return value === filterValue;
    case FilterTypes.NotEqual:
      return value !== filterValue;
    case FilterTypes.GreaterThan:
      return value > filterValue;
    case FilterTypes.GreaterThanOrEqual:
      return value >= filterValue;
    case FilterTypes.LessThan:
      return value < filterValue;
    case FilterTypes.LessThanOrEqual:
      return value <= filterValue;
    case FilterTypes.InRange:
      return value >= from && value <= to;
    default:
      return true;
  }
};

/**
 * The applyDateFilter function Applies a date filter to determine if the provided date
 * value satisfies the given conditions.
 *
 * @param value - The date value to be filtered (string or Date object).
 * @param filter - The filter object containing filter conditions.
 * @returns True if the date value satisfies the filter conditions, false otherwise.
 *
 * ```typescript
 *
 *  import {applyDateFilter} from '@ion/dls/src/grid/';
 *
 *  applyDateFilter('2022-01-15', {type: FilterTypes.Equals, dateFrom: '2022-01-15'}); // true
 *
 * ```
 */
export const applyDateFilter = (value: string | Date, filter: GenericObject) => {
  const fromDate    = new Date(filter.dateFrom);
  const toDate      = new Date(filter.dateTo);
  const valueInTime = dateNormalizer(value as any, UserPreferences.getSystemDateFormat());

  switch (filter.type) {
    case FilterTypes.Equals:
      return isSame(endOfDay(valueInTime), endOfDay(fromDate));
    case FilterTypes.NotEqual:
      return !isSame(endOfDay(valueInTime), endOfDay(fromDate));
    case FilterTypes.LessThan:
      return endOfDay(valueInTime) < startOfDay(fromDate);
    case FilterTypes.GreaterThan:
      return startOfDay(valueInTime) > endOfDay(fromDate);
    case FilterTypes.InRange:
      return (
        (startOfDay(valueInTime) > endOfDay(fromDate) && endOfDay(valueInTime) < startOfDay(toDate)) ||
        (isSame(endOfDay(valueInTime), endOfDay(fromDate)) || isSame(endOfDay(valueInTime), endOfDay(toDate)))
      );
    default:
      return true;
  }
};

/**
 * The dateNormalizer function returns a valid date.
 *
 * ```typescript
 *
 *  import {dateNormalizer} from '@ion/dls/src/grid/';
 *
 *  dateNormalizer('02/28/2024'); // Wed Feb 28 2024 00:00:00 GMT-0800
 *  dateNormalizer('28/02/2024'); // Wed Feb 28 2024 00:00:00 GMT-0800
 *
 * ```
 */
export const dateNormalizer = (value: string, inputFormat: string) => {
  if (inputFormat === DefaultDateFormats.EU && value) {
    const dateParts = value.split(value.charAt(2));
    const day       = parseInt(dateParts[0], 10);
    const month     = parseInt(dateParts[1], 10) - 1;
    const year      = parseInt(dateParts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(value);
};

/**
 * Waits for the specified page to load in the AG Grid and ensures that the data is fully loaded.
 *
 * This function returns a promise that resolves when both the pagination and the data
 * for the specified page have been fully loaded.
 *
 * @param gridApi - The AG Grid API instance.
 * @param pageNumber - The page number to wait for.
 * @returns A promise that resolves when the specified page and its data are fully loaded.
 *
 * ```typescript
 *
 *  import {waitForPageLoad} from '@ion/dls/src/grid/IonGridUtils';
 *
 *  waitForPageLoad(gridApi, 2).then(() => {
 *    console.log('Page 2 is fully loaded');
 *  });
 *
 * ```
 */
export const waitForPageLoad = (gridApi: AgGridType, pageNumber: number) => {
  return new Promise<void>((resolve) => {
    if (pageNumber === gridApi.paginationGetCurrentPage()) {
      resolve();
    }
    let pageLoaded = false;
    let dataLoaded = false;

    const checkPageLoaded = () => {
      const currentPage = gridApi.paginationGetCurrentPage();
      if (currentPage === pageNumber) {
        pageLoaded = true;
        checkBothLoaded();
      }
    };

    const checkDataLoaded = () => {
      dataLoaded = true;
      checkBothLoaded();
    };

    const checkBothLoaded = () => {
      if (pageLoaded && dataLoaded) {
        resolve();
        gridApi.removeEventListener('paginationChanged', checkPageLoaded);
        gridApi.removeEventListener('modelUpdated', checkDataLoaded);
      }
    };

    gridApi.addEventListener('paginationChanged', checkPageLoaded);
    gridApi.addEventListener('modelUpdated', checkDataLoaded);
    gridApi.paginationGoToPage(pageNumber);
    checkPageLoaded();
  });
};

/**
 * The getStatusFilters returns a string if filter is defined else it returns and array.
 *
 * ```typescript
 *
 *  import {getStatusFilters} from '@ion/dls/src/grid/';
 *
 *  getStatusFilters();
 *  getStatusFilters();
 *
 * ```
 */
export const getStatusFilters = (filter: String, item: Object, filterType: String) => {
  if (filterType === FilterTypes.Set) {
    const {values} = item[1] || {};
    return values?.toString();
  }
  return EntityStatus[getStatusFilter(filter, item)];
};

/**
 * Retrieves a proxy object representing the current page state of the grid.
 *
 * ```typescript
 *
 *  import {getPageProxy} from './IonGridUtils';
 *
 *  const {pageSize, currentPage} = getPageProxy(api, total);
 *
 * ```
 *
 * @param gridApi - The API instance of AG Grid.
 * @param total - The total number of rows in the dataset.
 * @returns An object containing pagination information including the current page, page size,
 *          and the index of the top and bottom displayed rows.
 */
export const getPageProxy = (gridApi: GridApi, total: number): PageProxy => {
  const currentPage = gridApi.paginationGetCurrentPage();
  const pageSize    = gridApi.paginationGetPageSize();
  const last        = (currentPage + 1) * pageSize;

  return {
    bottomDisplayedRowIndex : (last < total ? last : total) - 1,
    topDisplayedRowIndex    : currentPage * pageSize,
    currentPage,
    pageSize
  };
};

/**
 * Retrieves the current sort model from the AG Grid API.
 *
 * ```typescript
 *
 *  import {getSortModel} from './IonGridUtils';
 *
 *  const sort = getSortModel(api);
 *
 * ```
 *
 * @param api - The Column API instance from AG Grid.
 * @returns An array of objects representing the columns that are currently sorted, including their sort order.
 */
export const getSortModel = (api: GridApi): SortModel[] => {
  const columns = api.getColumnState();

  return columns.reduce<SortModel[]>((acc, column) => {
    const {colId, sort} = column;
    if (sort) {
      acc.push({colId, sort});
    }
    return acc;
  }, []);
};

/**
 * Retrieves all rows currently displayed in the grid after applying filters and sorting.
 *
 * ```typescript
 *
 *  import {getDisplayedRows} from './IonGridUtils';
 *
 *  const rows = getDisplayedRows(api);
 *
 * ```
 *
 * @param api - The Grid API instance from AG Grid.
 * @returns An array of RowNode objects representing the rows currently displayed in the grid.
 */
export const getDisplayedRows = (api: GridApi) => {
  const rows = [];
  api.forEachNodeAfterFilterAndSort((row) => rows.push(row));
  return rows;
};

/**
 * Validates a set filter for a specific column.
 *
 * @param column - The name of the column to validate.
 * @param filter - The filter object containing the values.
 * @returns A validation error message if applicable, otherwise null.
 */
export const validateSetFilter = (column, filter) => {
  if (column.toLowerCase().includes('status')) {
    const {values} = filter;
    if (!values?.length) {
      return translate('common:errorHandler.statusFilterMessage');
    }
  }
  return null;
};

/**
 * Validates the filter model provided in the request parameters.
 *
 * ```typescript
 *
 *  import {validateRequest} from './IonGridUtils';
 *
 *  const {valid, message} = validateRequest({filterModel});
 *
 * ```
 *
 * @param filterModel - The filter model to be validated.
 *
 * @returns {ValidateRequestResults} - The result of the validation, including validity status,
 *                                     a message, and all messages.
 */
export const validateRequest = ({filterModel}: ValidateRequestParams): ValidateRequestResults => {
  const filterErrors = Object.entries(filterModel).map(([column, filter]) => {
    switch (filter.filterType.toLowerCase()) {
      case 'set':
        return validateSetFilter(column, filter);
      default:
        return null;
    }
  }).filter(Boolean);
  return {
    valid       : isEmpty(filterErrors),
    message     : filterErrors[0],
    allMessages : filterErrors
  };
};
